import { httpClient } from '../client';
import { logger } from '../../../utils/logger';
import { ApiErrorHandler } from '../error.handler';
import { BaseEndpoint } from './base.endpoint';
import { MeetingProviderStatus } from '../../../types/meeting';

class MeetingEndpoints extends BaseEndpoint {
  private static instance: MeetingEndpoints;

  static getInstance(): MeetingEndpoints {
    if (!MeetingEndpoints.instance) {
      MeetingEndpoints.instance = new MeetingEndpoints();
    }
    return MeetingEndpoints.instance;
  }

  async getZoomAuthUrl(organizationId: string): Promise<string> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get(
        `${this.basePath}/client/${organizationId}/auth/meeting/zoom/url`,
        { headers }
      );
      logger.info('Zoom Auth URL fetched successfully');
      const data = response.data;
      if (typeof data === 'string') return data;
      if (data?.url) return data.url;
      if (data?.data && typeof data.data === 'string') return data.data;
      logger.error('Unexpected Zoom auth URL response format:', data);
      throw new Error('Invalid response format for Zoom auth URL');
    } catch (error) {
      logger.error('Failed to fetch Zoom Auth URL:', error);
      throw error;
    }
  }

  async getGoogleMeetAuthUrl(organizationId: string): Promise<string> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get(
        `${this.basePath}/client/${organizationId}/auth/meeting/google-meet/url`,
        { headers }
      );
      logger.info('Google Meet Auth URL fetched successfully');
      const data = response.data;
      if (typeof data === 'string') return data;
      if (data?.url) return data.url;
      if (data?.data && typeof data.data === 'string') return data.data;
      logger.error('Unexpected Google Meet auth URL response format:', data);
      throw new Error('Invalid response format for Google Meet auth URL');
    } catch (error) {
      logger.error('Failed to fetch Google Meet Auth URL:', error);
      throw error;
    }
  }

  async getMeetingProviders(organizationId: string): Promise<{ data: MeetingProviderStatus[] }> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get<{ data: MeetingProviderStatus[] }>(
        `${this.basePath}/organisations/${organizationId}/meeting/providers`,
        { headers }
      );
      logger.info('Meeting providers fetched successfully');
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch meeting providers:', error);
      return { data: [] };
    }
  }

  async disconnectProvider(organizationId: string, providerName: string): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      await httpClient.delete(
        `${this.basePath}/organisations/${organizationId}/meeting/provider/${providerName}`,
        { headers }
      );
      logger.info(`Successfully disconnected ${providerName}`);
    } catch (error) {
      logger.error(`Failed to disconnect ${providerName}:`, error);
      return ApiErrorHandler.handle(error);
    }
  }
}

export const meetingEndpoints = MeetingEndpoints.getInstance();
